import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { customerApi } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import { useInquiryCategories } from '../../components/inquiries/inquiryShared';

const BLANK = { name: '', phone: '', email: '', item_category: '', item_description: '' };

// General questions go into the same inquiry system as sell/pawn requests, so they get routed and answered the same way
function MessageForm() {
  const categories = useInquiryCategories();
  const [form, setForm] = useState(BLANK);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const r = await customerApi.post('/inquiries', { ...form, inquiry_type: 'question' });
      setSent(r.data.access_token);
    } catch (e) { setError(e.response?.data?.error || 'Something went wrong. Please try again.'); }
    setSaving(false);
  };

  if (sent) return (
    <div className="alert alert-success" style={{ padding: 20, textAlign: 'center' }}>
      <h3 style={{ fontWeight: 700, marginBottom: 8 }}>Message sent!</h3>
      <p style={{ fontSize: 14, color: '#555' }}>The right person on our team will reply on your conversation page{form.email ? ', and we\'ll email you when they do' : ''}.</p>
      <Link to={`/inquiry/${sent}`} className="btn btn-primary" style={{ marginTop: 12 }}>Open My Conversation →</Link>
    </div>
  );

  return (
    <form onSubmit={submit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="grid-2" style={{ gap: 12 }}>
        <div className="form-group"><label className="form-label">Name *</label><input className="form-control" required value={form.name} onChange={e => set('name', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
      </div>
      <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="So we can let you know when we reply" /></div>
      <div className="form-group">
        <label className="form-label">What's it about?</label>
        <select className="form-control" value={form.item_category} onChange={e => set('item_category', e.target.value)}>
          <option value="">General question</option>
          {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="form-group"><label className="form-label">Message *</label><textarea className="form-control" rows={4} required value={form.item_description} onChange={e => set('item_description', e.target.value)} /></div>
      <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%' }}>{saving ? 'Sending…' : 'Send Message'}</button>
    </form>
  );
}

export default function ContactPage() {
  const { settings, hours, todayLabel } = useSettings();
  const fullAddress = `${settings.store_address}, ${settings.store_city}, ${settings.store_state} ${settings.store_zip}`;
  const mapsQuery = encodeURIComponent(fullAddress);

  return (
    <div>
      <div className="page-hero">
        <h1>Contact Us</h1>
        <p>We're here {todayLabel}: {hours.find(([d]) => d === todayLabel)?.[1]} · {fullAddress}</p>
      </div>

      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div className="grid-2" style={{ gap: 52, alignItems: 'start' }}>

            {/* Contact cards */}
            <div>
              <h2 className="section-title">Get in Touch</h2>
              <div className="gold-divider" />

              {[
                { icon: '📞', label: 'Pawn Shop', value: settings.store_phone, href: `tel:${(settings.store_phone || '').replace(/\D/g, '')}`, sub: settings.store_name },
                { icon: '🔧', label: 'Tech Repair', value: settings.screen_savers_phone, href: `tel:${(settings.screen_savers_phone || '').replace(/\D/g, '')}`, sub: 'Screen Savers Phone Repair' },
                { icon: '✉️', label: 'Email', value: settings.store_email, href: `mailto:${settings.store_email}`, sub: 'We respond within 24 hours' },
                { icon: '🔧✉️', label: 'Tech Repair Email', value: settings.tech_repair_email, href: `mailto:${settings.tech_repair_email}`, sub: 'Screen Savers Phone Repair' },
                { icon: '📍', label: 'Address', value: settings.store_address, href: `https://maps.google.com/?q=${mapsQuery}`, sub: `${settings.store_city}, ${settings.store_state} ${settings.store_zip}`, target: '_blank' },
              ].map(c => (
                <a key={c.label} href={c.href} target={c.target} rel={c.target ? 'noreferrer' : undefined}
                  style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: '1px solid #e8e8e8', textDecoration: 'none' }}>
                  <div style={{ width: 44, height: 44, background: 'rgba(201,162,39,0.1)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{c.icon}</div>
                  <div>
                    <div style={{ fontSize: 11, color: '#9a9a9a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{c.label}</div>
                    <div style={{ fontWeight: 700, color: '#1a2744', fontSize: 16, marginBottom: 2 }}>{c.value}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{c.sub}</div>
                  </div>
                </a>
              ))}

              <div className="card" style={{ padding: 24, marginTop: 24 }}>
                <h3 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 4 }}>Send Us a Message</h3>
                <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Ask about an item, check on availability, or anything else.</p>
                <MessageForm />
              </div>

              {/* Social */}
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Follow Us</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ gap: 8 }}>Facebook</a>}
                  {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ gap: 8 }}>Instagram</a>}
                </div>
              </div>
            </div>

            {/* Hours + Map */}
            <div>
              <div className="card" style={{ padding: 24, marginBottom: 20 }}>
                <h3 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 16 }}>Hours of Operation</h3>
                {hours.map(([day, dayHours]) => (
                  <div key={day} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14, color: day === todayLabel ? '#c9a227' : '#444', fontWeight: day === todayLabel ? 700 : 400 }}>
                    <span>{day === todayLabel ? `${day} (Today)` : day}</span>
                    <span style={{ color: dayHours === 'Closed' ? '#c62828' : 'inherit' }}>{dayHours}</span>
                  </div>
                ))}
              </div>

              {/* Embedded map */}
              <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
                <iframe
                  title="Premier Pawn Location"
                  src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
                  width="100%" height="280" style={{ border: 0, display: 'block' }}
                  allowFullScreen loading="lazy" />
              </div>
              <a href={`https://maps.google.com/?q=${mapsQuery}`} target="_blank" rel="noreferrer"
                className="btn btn-outline" style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}>
                Get Directions →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
